import {
  FormDatePicker,
  FormNumber,
  FormPrice,
  RHF,
} from "@devhop/ui";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { Control, FieldValues, Path } from "react-hook-form";
import type { SellPackageDiscountType } from "./SellPackageDiscountTypeRadio";
import { SellPackageDiscountTypeRadio } from "./SellPackageDiscountTypeRadio";
import type { SellPackageSettlementMode } from "./SellPackageSettlementModeRadio";
import { SellPackageSettlementModeRadio } from "./SellPackageSettlementModeRadio";

export type SellPackagePriceSummary = {
  originalPrice: number;
  discountAmount: number;
  finalPrice: number;
};

type SellPackageUsagePeriodStepProps<T extends FieldValues> = {
  stepNumber: number;
  control: Control<T>;
  discountType: SellPackageDiscountType | undefined;
  settlementMode: SellPackageSettlementMode;
  expireDate?: string;
  validDaysBadge?: number;
  priceSummary?: SellPackagePriceSummary | null;
  usageHint?: string;
  showValidDays?: boolean;
  showPurchasedHours?: boolean;
  onDiscountTypeChange?: (next: SellPackageDiscountType) => void;
};

export function SellPackageUsagePeriodStep<T extends FieldValues>({
  stepNumber,
  control,
  discountType,
  settlementMode,
  expireDate,
  validDaysBadge,
  priceSummary,
  usageHint,
  showValidDays = true,
  showPurchasedHours = false,
  onDiscountTypeChange,
}: SellPackageUsagePeriodStepProps<T>) {
  const dt = discountType ?? "none";
  const finalPrice = priceSummary?.finalPrice ?? 0;

  return (
    <div className="rounded-xl border bg-card p-6">
      <div className="mb-4 flex items-center gap-3">
        <div className="flex size-8 items-center justify-center rounded-full bg-red-600 font-bold text-sm text-white">
          {stepNumber}
        </div>
        <h3 className="font-semibold text-lg">ໄລຍະເວລາການໃຊ້ງານ</h3>
      </div>

      {usageHint ? (
        <p className="mb-4 text-muted-foreground text-sm">{usageHint}</p>
      ) : (
        <p className="mb-4 text-muted-foreground text-sm">
          ວັນຫມົດອາຍຸ = ວັນເລີ່ມ + ຈຳນວນວັນທີ່ຕັ້ງຕອນຂາຍ.
        </p>
      )}

      <div className="space-y-4">
        {showPurchasedHours && (
          <FormNumber
            name={"purchasedHours" as Path<T>}
            label="ຈຳນວນຊົ່ວໂມງທີ່ຊື້"
            requiredMark
            min={1}
            step={1}
          />
        )}

        {showValidDays && (
          <FormNumber
            name={"validDays" as Path<T>}
            label="ຈຳນວນວັນທີ່ໃຊ້ໄດ້ (ນັບຈາກວັນເລີ່ມ)"
            requiredMark
            min={1}
            max={3650}
            step={1}
          />
        )}

        <div className="space-y-2">
          <FormDatePicker
            name={"startDate" as Path<T>}
            label="ວັນທີເລີ່ມຕົ້ນ"
            requiredMark
            placeholder="ເລືອກວັນທີເລີ່ມຕົ້ນ..."
          />
          <p className="text-muted-foreground text-xs">
            ມື້ນີ້ (ຕັ້ງເປັນຄ່າເລີ່ມຕົ້ນ)
          </p>
        </div>

        {expireDate && (
          <div className="space-y-2">
            <span className="block font-medium text-sm">ວັນທີ່ຫມົດອາຍຸ</span>
            <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-3 py-2">
              <CalendarIcon className="size-4 text-muted-foreground" />
              <span className="flex-1 text-sm">
                {format(new Date(expireDate), "MMM dd, yyyy")}
              </span>
              {validDaysBadge != null && validDaysBadge >= 1 && (
                <span className="rounded bg-red-100 px-2 py-1 font-medium text-red-700 text-xs">
                  +{validDaysBadge} Days
                </span>
              )}
            </div>
            <p className="text-muted-foreground text-xs">ຄ່າທີ່ຄິດໄລ່ອັດຕະໂນມັດ</p>
          </div>
        )}

        <div className="space-y-3 border-t pt-4">
          <RHF.Controller
            name={"discountType" as Path<T>}
            control={control}
            render={({ field }) => (
              <SellPackageDiscountTypeRadio
                value={(field.value as SellPackageDiscountType | undefined) ?? "none"}
                onChange={(next) => {
                  field.onChange(next);
                  onDiscountTypeChange?.(next);
                }}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />

          {(dt === "percentage" || dt === "fixed") && (
            <div className="max-w-sm">
              {dt === "percentage" ? (
                <FormNumber
                  name={"discountValue" as Path<T>}
                  label="ສ່ວນຫຼຸດ (%)"
                  placeholder="ປ້ອນສ່ວນຫຼຸດ (0-100)"
                  min={0}
                  max={100}
                />
              ) : (
                <FormPrice
                  name={"discountValue" as Path<T>}
                  label="ສ່ວນຫຼຸດ (ກີບ)"
                  placeholder="ປ້ອນຈຳນວນເງິນ"
                  currencySymbol="₭"
                />
              )}
            </div>
          )}
        </div>

        <div className="space-y-3 border-t pt-4">
          <RHF.Controller
            name={"settlementMode" as Path<T>}
            control={control}
            render={({ field }) => (
              <SellPackageSettlementModeRadio
                value={field.value as SellPackageSettlementMode}
                onChange={field.onChange}
                onBlur={field.onBlur}
                name={field.name}
              />
            )}
          />

          {settlementMode === "credit" && finalPrice > 0 && (
            <FormPrice
              name={"amountPaidNow" as Path<T>}
              label="ຈຳນວນທີ່ຮັບຕອນຂາຍ — 0 ຖ້າຍັງບໍ່ຮັບ"
              placeholder="0"
              currencySymbol="₭"
              min={0}
              max={finalPrice}
              step={1}
            />
          )}
          {settlementMode === "credit" && finalPrice <= 0 && (
            <p className="text-muted-foreground text-xs">
              ລາຄາສຸດທ້າຍເປັນ 0 — ບໍ່ສ້າງໃບເກັບເງິນ; ແພັກເກດເປີດໃຊ້ງານຕາມປົກກະຕິ
            </p>
          )}
        </div>

        {priceSummary && (
          <div className="rounded-lg border bg-muted/50 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">ຍອດເງິນ:</span>
              <span className="font-semibold">
                {priceSummary.originalPrice.toLocaleString("lo-LA")} ກີບ
              </span>
            </div>
            {priceSummary.discountAmount > 0 && (
              <div className="mt-1 flex justify-between text-green-600">
                <span>ສ່ວນຫຼຸດ:</span>
                <span className="font-semibold">
                  -{priceSummary.discountAmount.toLocaleString("lo-LA")} ກີບ
                </span>
              </div>
            )}
            <div className="mt-2 flex justify-between border-t pt-2">
              <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
              <span className="font-bold text-red-600">
                {priceSummary.finalPrice.toLocaleString("lo-LA")} ກີບ
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
